import { ApolloError, useQuery } from '@apollo/client'
import { useLoginInfo } from 'app/_hooks'
import { useCallback, useMemo } from 'react'
import {
  GetOriginalVideoCookieDocument,
  GetOriginalVideoCookieQuery,
  GetOriginalVideoDocument,
  GetOriginalVideoQuery,
} from '~/__generated__/graphql'

export type VideoUnavailableReason =
  | 'REQUIRE_AUTH'
  | 'ALREADY_WITHDRAWAL'
  | 'REQUIRE_RESUBSCRIPTION'
  | 'REQUIRE_SUBSCRIPTION'
  | 'INVALID_CHARGE'

function isVideoUnavailableReason(value: any): value is VideoUnavailableReason {
  return [
    'REQUIRE_AUTH',
    'ALREADY_WITHDRAWAL',
    'REQUIRE_RESUBSCRIPTION',
    'REQUIRE_SUBSCRIPTION',
    'INVALID_CHARGE',
  ].includes(value)
}

export const useVideoDetail = (id: number | string | undefined) => {
  const loginInfo = useLoginInfo()
  const res = useQuery<GetOriginalVideoQuery>(GetOriginalVideoDocument, {
    variables: { id: id },
    fetchPolicy: 'no-cache', // originalVideo.viewingTimeが変化するため、常に最新のデータを取得する
  })
  const {
    error,
    data,
    refetch: refetchCookie,
  } = useQuery<GetOriginalVideoCookieQuery>(GetOriginalVideoCookieDocument, {
    variables: { id: id },
  })
  const unavailableReason = useMemo(() => {
    if (error) {
      return findVideoUnavailableReason(error)
    }
  }, [error])
  const isTrailer =
    !error && !data ? undefined : loginInfo.status !== 'signedIn' || unavailableReason != undefined

  const refetch = useCallback(async () => {
    await Promise.all([res.refetch(), refetchCookie()])
  }, [res, refetchCookie])

  return {
    ...res,
    unavailableReason,
    cookie: data?.originalVideo?.cookies?.headerValue,
    isTrailer,
    refetch,
  }
}

const findVideoUnavailableReason = (error: ApolloError) => {
  const err = error.graphQLErrors.find((err) => err.extensions?.['code'] == 'VIDEO_UNAVAILABLE')
  const reason = err?.extensions?.['reason']
  return isVideoUnavailableReason(reason) ? reason : undefined
}
